/**
 * ComparisonRow Component
 *
 * Single field comparison row with CSS grid layout.
 * Shows field label, left value, separator, right value, and optional delta.
 */

import React from 'react'
import { Typography, Tooltip, Tag } from 'antd'
import { ArrowRightOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons'
import type { FieldMetadata } from '@/services/metadataService'
import type { ComparisonRowProps } from './types'
import {
  formatCellValue,
  isLifecycleField,
  renderLifecycleStatus,
} from '@/components/MetadataTable/utils/columnFormatters'
import { isJsonField, isJsonValue } from '@/components/MetadataTable/utils/jsonFormatters'
import { JsonValueDisplay } from '@/components/MetadataTable/components/JsonValueDisplay'
import { formatNumericDelta, formatPercentDelta, getFieldTitle } from './utils/formatters'

const { Text } = Typography

/**
 * Render a field value with appropriate formatting.
 */
function renderFieldValue(
  value: unknown,
  fieldKey: string,
  metadata?: FieldMetadata
): React.ReactNode {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return <Text type="secondary" italic>--</Text>
  }

  // Handle lifecycle state fields specially
  if (metadata && isLifecycleField(fieldKey, metadata) && typeof value === 'string') {
    return renderLifecycleStatus(value)
  }

  // JSON fields - use interactive popover display
  if ((metadata && isJsonField(metadata)) || isJsonValue(value)) {
    return <JsonValueDisplay value={value} maxHeight={200} collapsed={1} />
  }

  // Use column formatter for other fields
  if (metadata) {
    const formatted = formatCellValue(value, metadata)
    return <Text>{formatted}</Text>
  }

  return <Text>{String(value)}</Text>
}

/**
 * Single field comparison row.
 *
 * Layout uses CSS grid with 5 columns:
 * - Field label (20%)
 * - Left value (1fr)
 * - Separator (32px)
 * - Right value (1fr)
 * - Delta (auto)
 */
export function ComparisonRow({
  difference,
  metadata,
  isKeyField = false,
  mode,
  hasComparison,
  temporalContext,
  // Colors passed through but not used in rows - header-only treatment
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  leftColumnColor: _leftColumnColor,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  rightColumnColor: _rightColumnColor,
}: ComparisonRowProps): JSX.Element {
  const {
    fieldKey,
    leftValue,
    rightValue,
    isDifferent,
    numericDelta,
    percentDelta,
  } = difference

  const title = getFieldTitle(fieldKey, metadata)
  const hasNumericDelta = numericDelta !== undefined && numericDelta !== 0

  // Determine delta direction for styling
  const deltaDirection = numericDelta
    ? numericDelta > 0
      ? 'increase'
      : 'decrease'
    : null

  // In directed mode, separator is arrow; in arbitrary mode, it's "vs"
  const separator = mode === 'directed' ? (
    <ArrowRightOutlined style={{ fontSize: 14 }} />
  ) : (
    <Text type="secondary" style={{ fontSize: 12 }}>vs</Text>
  )

  // Highlighting behavior differs by mode
  // - directed: Only right column (new value) highlighted when different
  // - arbitrary: Both columns highlighted when different
  const highlightLeft = mode === 'arbitrary' && isDifferent
  const highlightRight = isDifferent

  // Build accessible label for screen readers
  const accessibleLabel = hasComparison && isDifferent
    ? `${title}${isKeyField ? ' (key field)' : ''}: changed from ${String(leftValue ?? 'empty')} to ${String(rightValue ?? 'empty')}${hasNumericDelta && metadata ? `, delta ${formatNumericDelta(numericDelta!, metadata)}` : ''}`
    : `${title}: ${String(leftValue ?? 'empty')}`

  // Row class includes no-comparison modifier for CSS grid layout
  const rowClasses = [
    'comparison-row',
    isDifferent ? 'is-different' : '',
    isKeyField ? 'is-key-field' : '',
    !hasComparison ? 'no-comparison' : '',
  ].filter(Boolean).join(' ')

  return (
    <div
      role="listitem"
      aria-label={accessibleLabel}
      className={rowClasses}
    >
      {/* Field Label */}
      <div className="comparison-row-label">
        <Tooltip title={metadata?.description || undefined}>
          <Text
            strong={isKeyField}
            style={{
              fontSize: 13,
              color: isKeyField ? 'var(--rp-blue-primary)' : undefined,
            }}
          >
            {title}
            {isKeyField && (
              <Tag
                color="blue"
                style={{
                  marginLeft: 6,
                  fontSize: 10,
                  padding: '0 4px',
                  lineHeight: '16px',
                }}
              >
                Key
              </Tag>
            )}
          </Text>
        </Tooltip>
      </div>

      {/* Left Value (Current/Baseline) */}
      <div className={`comparison-row-value comparison-row-left ${highlightLeft ? 'is-highlighted' : ''}`}>
        <div className="comparison-row-value-content">
          {renderFieldValue(leftValue, fieldKey, metadata)}
        </div>
      </div>

      {/* Separator - only shown when comparison exists */}
      {hasComparison && (
        <div
          className="comparison-row-separator"
          aria-hidden="true"
          style={{
            color: isDifferent ? 'var(--rp-gray-700)' : 'var(--rp-border-subtle)',
          }}
        >
          {separator}
        </div>
      )}

      {/* Right Value (New/Revised) - only shown when comparison exists */}
      {hasComparison && (
        <div className={`comparison-row-value comparison-row-right ${highlightRight ? 'is-highlighted' : ''}`}>
          <div className="comparison-row-value-content">
            {renderFieldValue(rightValue, fieldKey, metadata)}
          </div>
        </div>
      )}

      {/* Numeric Delta - only shown when comparison exists */}
      {hasComparison && (
        <div className="comparison-row-delta">
          {hasNumericDelta && metadata && (
            <Tooltip
              title={
                // Use temporal tooltip if context available, otherwise fall back to % change
                temporalContext
                  ? `Later record (${temporalContext.rightDateLabel}) is ${formatNumericDelta(Math.abs(numericDelta!), metadata)} ${numericDelta! > 0 ? 'higher' : 'lower'} than earlier record (${temporalContext.leftDateLabel})`
                  : percentDelta !== undefined
                    ? `${formatPercentDelta(percentDelta)} change`
                    : undefined
              }
            >
              <Tag
                color={deltaDirection === 'increase' ? 'orange' : 'green'}
                icon={
                  deltaDirection === 'increase' ? (
                    <ArrowUpOutlined />
                  ) : (
                    <ArrowDownOutlined />
                  )
                }
                style={{
                  fontSize: 12,
                  marginRight: 0,
                }}
              >
                {formatNumericDelta(numericDelta!, metadata)}
                {percentDelta !== undefined && (
                  <span style={{ marginLeft: 4, opacity: 0.8 }}>
                    ({formatPercentDelta(percentDelta)})
                  </span>
                )}
              </Tag>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  )
}

export default ComparisonRow
